import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import MultiWebsiteSelector from '../analytics/MultiWebsiteSelector';
import ActivityContractSelector from '../analytics/ActivityContractSelector';
import { getDefaultDateRange } from '../../utils/dateUtils';

const FilterPopup = ({ isOpen, onClose, onFiltersChange, appliedFilters, teamId }) => {
  // Filter state
  const [selectedWebsites, setSelectedWebsites] = useState(appliedFilters?.websites || []);
  const [selectedContracts, setSelectedContracts] = useState(appliedFilters?.contracts || []);
  const [dateRange] = useState(getDefaultDateRange());
  
  // Section collapse state
  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    source: false
  });

  // Sync with applied filters when popup opens
  useEffect(() => {
    if (isOpen && appliedFilters) {
      setSelectedWebsites(appliedFilters.websites || []);
      setSelectedContracts(appliedFilters.contracts || []);
    }
  }, [isOpen, appliedFilters]);

  const toggleSection = (section) => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleApplyFilters = () => {
    const filters = {
      websites: selectedWebsites,
      contracts: selectedContracts,
      sessions: [], // Keep empty for compatibility
      userIdentities: [], // Keep empty for compatibility
      dateRange
    };
    
    onFiltersChange?.(filters);
    onClose();
  };

  const handleResetFilters = () => {
    setSelectedWebsites([]);
    setSelectedContracts([]);
  };

  const hasActiveFilters = () => {
    return selectedWebsites.length > 0 ||
           selectedContracts.length > 0;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl rounded-lg" style={{ overflow: 'visible' }}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0" style={{ zIndex: 1 }}>
          <h2 className="text-lg font-semibold text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
            Overview Filters
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 text-[#475569] hover:text-[#475569] transition-colors rounded"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1" style={{ overflow: 'visible' }}>
          {/* Source Section */}
          <div className="border-b border-gray-200">
            <button
              onClick={() => toggleSection('source')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-md font-medium text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Source
              </h3>
              {sectionsCollapsed.source ? 
                <ChevronRight className="h-5 w-5 text-[#475569]" /> : 
                <ChevronDown className="h-5 w-5 text-[#475569]" />
              }
            </button>
            
            {!sectionsCollapsed.source && (
              <div className="px-6 pb-4 space-y-4" style={{ position: 'relative', zIndex: 10 }}>
                {/* Websites Subsection */}
                <div style={{ position: 'relative', zIndex: 110 }}>
                  <label className="block text-sm font-medium text-[#475569] mb-2">
                    Websites
                  </label>
                  <MultiWebsiteSelector
                    selectedSiteIds={selectedWebsites}
                    onSiteChange={setSelectedWebsites}
                    teamId={teamId}
                    placeholder="Select websites"
                    showLabel={false}
                    selectFirstOnly={true}
                  />
                </div>
                
                {/* Smart Contracts Subsection */}
                <div style={{ position: 'relative', zIndex: 100 }}>
                  <label className="block text-sm font-medium text-[#475569] mb-2">
                    Smart Contracts
                  </label>
                  <ActivityContractSelector
                    selectedContracts={selectedContracts}
                    onContractChange={setSelectedContracts}
                    teamId={teamId}
                    placeholder="Select smart contracts"
                    showLabel={false}
                    selectFirstOnly={true}
                  />
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0" style={{ zIndex: 1 }}>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#0f172a] transition-colors"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            Reset All
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[#475569] border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              Cancel
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 text-sm font-medium text-white bg-[#1d0c46] rounded-md hover:bg-[#1d0c46]/90 transition-colors"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              Apply Filters {hasActiveFilters() && '•'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPopup;

